import { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { url } from '../env';

const headers = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Accept': 'application/json',
    'Content-Type': 'application/json',
});

export const useQuoteAdjustmentDraft = () => {
    // Separate loading states to avoid concurrent operations overwriting each other
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const activeOps = useRef(0);

    const startOp = () => {
        activeOps.current++;
        setLoading(true);
        setError(null);
    };

    const endOp = () => {
        activeOps.current--;
        if (activeOps.current <= 0) {
            activeOps.current = 0;
            setLoading(false);
        }
    };

    const createDraft = useCallback(async ({ quoteProcessId, budgetId, sourceInvoiceFileId, comparisonType, invoiceSnapshot }) => {
        startOp();
        try {
            const response = await axios.post(`${url}/quote-adjustment-drafts`, {
                quote_process_id: quoteProcessId,
                budget_id: budgetId,
                source_invoice_file_id: sourceInvoiceFileId,
                comparison_type: comparisonType,
                invoice_snapshot: invoiceSnapshot,
            }, { headers: headers() });

            return response.data;
        } catch (err) {
            const msg = err.response?.data?.message || err.message;
            setError(msg);
            throw err;
        } finally {
            endOp();
        }
    }, []);

    const updateDraft = useCallback(async (draftId, data) => {
        startOp();
        try {
            const response = await axios.put(`${url}/quote-adjustment-drafts/${draftId}`, data, { headers: headers() });
            return response.data;
        } catch (err) {
            setError(err.response?.data?.message || err.message);
            throw err;
        } finally {
            endOp();
        }
    }, []);

    const markReady = useCallback(async (draftId) => {
        startOp();
        try {
            const response = await axios.post(`${url}/quote-adjustment-drafts/${draftId}/mark-ready`, {}, { headers: headers() });
            return response.data;
        } catch (err) {
            setError(err.response?.data?.message || err.message);
            throw err;
        } finally {
            endOp();
        }
    }, []);

    const discardDraft = useCallback(async (draftId) => {
        startOp();
        try {
            const response = await axios.post(`${url}/quote-adjustment-drafts/${draftId}/discard`, {}, { headers: headers() });
            return response.data;
        } catch (err) {
            setError(err.response?.data?.message || err.message);
            throw err;
        } finally {
            endOp();
        }
    }, []);

    const approveDraft = useCallback(async (draftId) => {
        startOp();
        try {
            const response = await axios.post(`${url}/quote-adjustment-drafts/${draftId}/approve`, {}, { headers: headers() });
            return response.data;
        } catch (err) {
            setError(err.response?.data?.message || err.message);
            throw err;
        } finally {
            endOp();
        }
    }, []);

    const getDraftsByBudget = useCallback(async (budgetId) => {
        startOp();
        try {
            const response = await axios.get(`${url}/quote-adjustment-drafts/budget/${budgetId}`, { headers: headers() });
            return response.data;
        } catch (err) {
            setError(err.response?.data?.message || err.message);
            throw err;
        } finally {
            endOp();
        }
    }, []);

    const getActiveDraftByBudget = useCallback(async (budgetId) => {
        const result = await getDraftsByBudget(budgetId);
        if (result?.success && result.data) {
            return result.data.find(d => ['draft', 'ready_for_review'].includes(d.status)) || null;
        }
        return null;
    }, [getDraftsByBudget]);

    const submitPreview = useCallback(async (params) => {
        startOp();
        try {
            const response = await axios.post(`${url}/cotizador2-preview`, params, { headers: headers() });
            return response.data;
        } catch (err) {
            setError(err.response?.data?.message || err.message);
            throw err;
        } finally {
            endOp();
        }
    }, []);

    return {
        loading,
        error,
        createDraft,
        updateDraft,
        markReady,
        discardDraft,
        approveDraft,
        getDraftsByBudget,
        getActiveDraftByBudget,
        submitPreview,
    };
};
